import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAtomValue } from 'jotai';
import debounce from 'lodash/debounce';

import { SWING_FOUNDATIONS_KIOSK, useUpdateLayout } from '@queries';
import {
    activeUserIdAtom,
    layoutAtom,
    layoutFloorModulesAtom,
    layoutKeyParameterAtom,
    layoutKioskModulesAtom,
    layoutKioskModulesMapAtom,
} from '@store';

const keysEqual = (a: string[], b: string[]) => (a.length !== b.length ? false : a.every((val, i) => val === b[i]));

export const useModuleOrdering = () => {
    const activeUserId = useAtomValue(activeUserIdAtom);
    const kioskModules = useAtomValue(layoutKioskModulesAtom);
    const kioskModulesMap = useAtomValue(layoutKioskModulesMapAtom);
    const shouldSyncRef = useRef<boolean>(false);
    const [kioskModuleIds, setKioskModuleIds] = useState(kioskModules.map((m) => m.childUiNodeId));

    useEffect(() => {
        shouldSyncRef.current = false;
        const newKioskModuleIds = kioskModules.map((m) => m.childUiNodeId);
        setKioskModuleIds((prev) => (keysEqual(prev, newKioskModuleIds) ? prev : newKioskModuleIds));
    }, [kioskModules]);

    const layout = useAtomValue(layoutAtom);
    const keyParameter = useAtomValue(layoutKeyParameterAtom);
    const floorModules = useAtomValue(layoutFloorModulesAtom);
    const { mutateAsync: updateLayout, isPending } = useUpdateLayout();

    const syncLayout = useCallback(
        async (orderedModuleIds: string[]) => {
            const layoutId = layout?.id;
            const layoutOwner = layout?.ownerUserId;
            const layoutName = layout?.name;
            if (!layoutId || !layoutName || !layoutOwner) {
                throw new Error('Layout not found');
            }
            const keyParameterId = keyParameter?.childUiNodeId;
            if (!keyParameterId) {
                throw new Error('Layout key parameter not found');
            }
            const kioskModuleOrder = [SWING_FOUNDATIONS_KIOSK, ...orderedModuleIds];
            const floorModuleOrder = floorModules.map((c) => c.childUiNodeId);
            await updateLayout({
                layoutId,
                keyParameterId,
                name: layoutName,
                userId: activeUserId,
                customizations: {
                    kiosk: kioskModuleOrder,
                    floor: floorModuleOrder,
                },
            });
        },
        [
            activeUserId,
            floorModules,
            keyParameter?.childUiNodeId,
            layout?.id,
            layout?.name,
            layout?.ownerUserId,
            updateLayout,
        ],
    );

    const debouncedSyncLayout = useMemo(() => debounce(syncLayout, 3_000), [syncLayout]);

    useEffect(() => {
        if (shouldSyncRef.current) {
            debouncedSyncLayout(kioskModuleIds);
        }
        return () => {
            debouncedSyncLayout.cancel();
        };
    }, [debouncedSyncLayout, kioskModuleIds]);

    const orderedKioskModules = useMemo(
        () => kioskModuleIds.map((id) => kioskModulesMap[id]).filter(Boolean),
        [kioskModuleIds, kioskModulesMap],
    );

    const handleReorder = useCallback((newOrderedIds: string[]) => {
        shouldSyncRef.current = true;
        setKioskModuleIds(newOrderedIds);
    }, []);

    const handleRemove = useCallback((idToRemove: string) => {
        shouldSyncRef.current = true;
        setKioskModuleIds((prev) => prev.filter((id) => id !== idToRemove));
    }, []);

    const [addItemIndex, setAddItemIndex] = useState<number | null>(null);

    const handleAdd = useCallback(
        (idToAdd: string) => {
            if (addItemIndex === null) {
                throw new Error('No module position found');
            }
            syncLayout([...kioskModuleIds.slice(0, addItemIndex), idToAdd, ...kioskModuleIds.slice(addItemIndex)]);
            setAddItemIndex(null);
        },
        [addItemIndex, kioskModuleIds, syncLayout],
    );

    return {
        kioskModules: orderedKioskModules,
        kioskModuleIds,
        handleReorder,
        handleRemove,
        handleAdd,
        isLoading: isPending,
        setAddItemIndex,
        addItemIndex,
    };
};
